<template>
  <div class="condition-insights">
    <div class="ci-head">
      <div class="ci-title">
        <i class="fa-duotone fa-chart-line"></i>
        Condition Insights
      </div>
    </div>

    <div class="ci-toprow">
  
      <PendingConditionsAfterApprovalSummaryCard :authToken="authToken" />
      <SwiftAiNotClearedSummaryCard :authToken="authToken" />
      <CtcWizardSummaryCard :authToken="authToken" />
    </div>

    <div class="ci-body-head">
      <div class="ci-body-head-left">
        <div class="ci-body-head-title">Timeframe</div>
        <div class="ci-body-head-sub">{{ chartRangeLabel }}</div>
      </div>

      <select class="ci-select" v-model="selectedTimeframe" :disabled="loading">
        <option v-for="opt in timeframeOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
    </div>

    <div class="ci-body">
      <div class="ci-left">
        <div class="ci-summary-grid">
              <SubmittalCounterSummaryCard :authToken="authToken" />
          <div class="ci-summary-group">
            <div class="ci-summary-title ci-card-title ci-accent ci-accent--productivity">
              Initial Underwrites Performance
            </div>

            <div class="ci-summary-cards ci-scroll">
              <div v-if="loading" class="ci-summary-loading">Loading...</div>

              <template v-else>
                <div v-for="s in initialUwSummary" :key="s.key" class="ci-summary-row">
                  <div class="ci-summary-main">
                    <div class="ci-summary-name">{{ s.name }}</div>

                    <div class="ci-summary-submetrics">
                      <div class="ci-summary-submetric">
                        <span class="ci-pill-label">Loans</span>
                        <span class="ci-pill-value">{{ s.loans }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="!initialUwSummary.length" class="ci-empty-inline">
                  No activity in this range
                </div>
              </template>
            </div>
          </div>

          <div class="ci-summary-group">
            <div class="ci-summary-title ci-card-title ci-accent ci-accent--success">
              Conditions Cleared Performance
            </div>

            <div class="ci-summary-cards ci-scroll">
              <div v-if="loading" class="ci-summary-loading">Loading...</div>

              <template v-else>
                <div v-for="s in conditionsSummary" :key="s.key" class="ci-summary-row">
                  <div class="ci-summary-main">
                    <div class="ci-summary-name">{{ s.name }}</div>

                    <div class="ci-summary-submetrics">
                      <div class="ci-summary-submetric">
                        <span class="ci-pill-label">Cleared</span>
                        <span class="ci-pill-value">{{ s.events }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="!conditionsSummary.length" class="ci-empty-inline">
                  No activity in this range
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="ci-right">
        <div class="ci-chart-card">
          <div class="ci-chart-card-head ci-card-title ci-accent ci-accent--insight">
            <div class="ci-chart-card-head-left">
              <div class="ci-chart-card-title">
                <i class="fa-duotone fa-chart-line"></i>
                {{ activeChart === "uw" ? "Initial Underwrites" : "Conditions Cleared" }}
              </div>
              <div class="ci-chart-card-sub">{{ chartSubtitle }}</div>
            </div>

            <div class="ci-chart-toggle" role="tablist" aria-label="Chart toggle">
              <button
                type="button"
                class="ci-toggle-btn"
                :disabled="loading"
                :class="{ active: activeChart === 'uw' }"
                @click="setActiveChart('uw')"
              >
                Initial UWs
              </button>

              <button
                type="button"
                class="ci-toggle-btn"
                :disabled="loading"
                :class="{ active: activeChart === 'cond' }"
                @click="setActiveChart('cond')"
              >
                Conditions
              </button>
            </div>
          </div>

          <div class="ci-chart-card-body">
            <div class="ci-chart-wrap">
              <InitialUwRollingChartCard
                v-if="activeChart === 'uw'"
                :key="chartKey"
                :azureId="azureId"
                :authToken="authToken"
                :orgId="orgId"
                :trendRows="initialUwTrendRows"
                :loadingExternal="loading"
                :titleOverride="`Initial Underwrites (${timeframeLabel})`"
                :emptyOverride="`No initial underwrites in ${timeframeLabel.toLowerCase()}`"
              />

              <ConditionsUwRollingChartCard
                v-else
                :key="chartKey"
                :azureId="azureId"
                :authToken="authToken"
                :orgId="orgId"
                :trendRows="conditionsTrendRows"
                :loadingExternal="loading"
                :titleOverride="`Conditions Cleared (${timeframeLabel})`"
                :emptyOverride="`No conditions cleared in ${timeframeLabel.toLowerCase()}`"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"

import InitialUwRollingChartCard from "@/components/Insights/InitialUwRollingChartCard.vue"
import ConditionsUwRollingChartCard from "@/components/Insights/ConditionsUwRollingChartCard.vue"
import SubmittalCounterSummaryCard from "@/components/Insights/SubmittalCounterSummaryCard.vue"
import PendingConditionsAfterApprovalSummaryCard from "@/components/Insights/PendingConditionsAfterApprovalSummaryCard.vue"
import SwiftAiClearedSummaryCard from "@/components/Insights/SwiftAiClearedSummaryCard.vue"
import SwiftAiNotClearedSummaryCard from "@/components/Insights/SwiftAiNotClearedSummaryCard.vue"
import CtcWizardSummaryCard from "@/components/Insights/CtcWizardSummaryCard.vue"
export default {
  name: "ConditionInsights",
  components: {
    InitialUwRollingChartCard,
    ConditionsUwRollingChartCard,
    SubmittalCounterSummaryCard,
    PendingConditionsAfterApprovalSummaryCard,
    SwiftAiClearedSummaryCard,
    SwiftAiNotClearedSummaryCard,
    CtcWizardSummaryCard
  },
  props: {
    azureId: { type: String, default: "" },
    authToken: { type: String, default: "" },
    orgId: { type: [Number, String], default: null }
  },
  data() {
    return {
      loading: false,

      initialUwTrendRows: [],
      conditionsTrendRows: [],

      initialUwLeaderboard: [],
      conditionsLeaderboard: [],

      activeChart: "uw",
      chartKey: 1,

      requestSeq: 0,

      selectedTimeframe: "rolling90",
      timeframeOptions: [
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "thisWeek", label: "This week" },
        { value: "lastWeek", label: "Last week" },
        { value: "thisMonth", label: "This month" },
        { value: "lastMonth", label: "Last month" },
        { value: "rolling90", label: "Rolling 90" }
      ]
    }
  },
  computed: {
    timeframeLabel() {
      return this.timeframeOptions.find(x => x.value === this.selectedTimeframe)?.label || "Rolling 90"
    },
    chartSubtitle() {
      const tf = this.timeframeLabel
      if (this.selectedTimeframe === "rolling90") return "Rolling 90 days"
      return tf
    },
    chartRange() {
      const now = new Date()
      const endOfDay = d => {
        const x = new Date(d)
        x.setHours(23, 59, 59, 999)
        return x
      }
      const startOfDay = d => {
        const x = new Date(d)
        x.setHours(0, 0, 0, 0)
        return x
      }
      const addDays = (d, days) => {
        const x = new Date(d)
        x.setDate(x.getDate() + days)
        return x
      }
      const startOfWeekMon = d => {
        const x = startOfDay(d)
        const day = x.getDay()
        const diff = (day + 6) % 7
        x.setDate(x.getDate() - diff)
        return x
      }
      const startOfMonth = d => {
        const x = startOfDay(d)
        x.setDate(1)
        return x
      }

      if (this.selectedTimeframe === "today") {
        return { start: startOfDay(now), end: endOfDay(now) }
      }

      if (this.selectedTimeframe === "yesterday") {
        const y = addDays(now, -1)
        return { start: startOfDay(y), end: endOfDay(y) }
      }

      if (this.selectedTimeframe === "thisWeek") {
        const s = startOfWeekMon(now)
        return { start: s, end: endOfDay(now) }
      }

      if (this.selectedTimeframe === "lastWeek") {
        const thisMon = startOfWeekMon(now)
        const lastMon = addDays(thisMon, -7)
        const lastSun = addDays(thisMon, -1)
        return { start: startOfDay(lastMon), end: endOfDay(lastSun) }
      }

      if (this.selectedTimeframe === "thisMonth") {
        const s = startOfMonth(now)
        return { start: s, end: endOfDay(now) }
      }

      if (this.selectedTimeframe === "lastMonth") {
        const thisM = startOfMonth(now)
        const lastMonthEnd = addDays(thisM, -1)
        const lastMonthStart = startOfMonth(lastMonthEnd)
        return { start: startOfDay(lastMonthStart), end: endOfDay(lastMonthEnd) }
      }

      const e = endOfDay(now)
      const s = startOfDay(addDays(e, -89))
      return { start: s, end: e }
    },
    chartRangeLabel() {
      const { start, end } = this.chartRange
      return `${this.timeframeLabel} • ${this.shortDate(start)} - ${this.shortDate(end)}`
    },

    initialUwSummary() {
      const fromLb = this.buildSummaryFromLeaderboard(this.initialUwLeaderboard, {
        eventsField: "totalInitialEvents",
        fallbackEventsField: "initialEventCount",
        loansField: "totalDistinctLoans"
      })
      if (fromLb.length) return fromLb
      return this.buildSummaryFromTrend(this.initialUwTrendRows, {
        eventsField: "initialEventCount",
        loansField: "initialDistinctLoans"
      })
    },

    conditionsSummary() {
      const fromLb = this.buildSummaryFromLeaderboard(this.conditionsLeaderboard, {
        eventsField: "totalConditionsCleared",
        fallbackEventsField: "conditionsCleared",
        loansField: "totalDistinctLoans"
      })
      if (fromLb.length) return fromLb
      return this.buildSummaryFromTrend(this.conditionsTrendRows, {
        eventsField: "initialEventCount",
        loansField: "initialDistinctLoans"
      })
    }
  },
  methods: {
    setActiveChart(which) {
      if (this.loading) return
      const next = which === "cond" ? "cond" : "uw"
      if (this.activeChart === next) return
      this.activeChart = next
      this.chartKey += 1
    },

    shortDate(d) {
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      const yyyy = d.getFullYear()
      return `${mm}/${dd}/${yyyy}`
    },

    isValidDateValue(v) {
      if (!v) return false
      const s = String(v)
      if (s.startsWith("0001-01-01")) return false
      const d = new Date(v)
      return !isNaN(d.getTime())
    },

    extractRoot(payload) {
      return payload?.data?.data || payload?.data || payload || {}
    },

    extractDailyTrend(payload) {
      const root = this.extractRoot(payload)
      const rows = root.dailyTrend || root.trend || root.trendRows || root.daily || []
      return Array.isArray(rows) ? rows : []
    },

    extractLeaderboard(payload) {
      const root = this.extractRoot(payload)
      const rows = root.leaderboard || root.leaderboardRows || root.summary || []
      return Array.isArray(rows) ? rows : []
    },

    toInitialUwChartRows(rows) {
      return (rows || [])
        .map(x => {
          const d = x.initialUwDate || x.trendDate || x.dateValue || x.date
          return { ...x, dateValue: this.isValidDateValue(d) ? d : null }
        })
        .filter(x => x.dateValue)
    },

    toConditionsClearedChartRows(rows) {
      return (rows || [])
        .map(x => {
          const trend = x.trendDate
          const initial = x.initialUwDate
          const dateValue = this.isValidDateValue(trend)
            ? trend
            : this.isValidDateValue(initial)
              ? initial
              : null

          return {
            ...x,
            dateValue,
            initialEventCount: Number(x.conditionsCleared || 0),
            initialDistinctLoans: Number(x.initialDistinctLoans || 0)
          }
        })
        .filter(x => x.dateValue)
    },

    buildSummaryFromLeaderboard(rows, opts) {
      const eventsField = opts?.eventsField || "totalConditionsCleared"
      const fallbackEventsField = opts?.fallbackEventsField || "conditionsCleared"
      const loansField = opts?.loansField || "totalDistinctLoans"

      return (rows || [])
        .map(r => {
          const key = r.underwriterAzureId || r.underwriterDisplayName || "unknown"
          const name = r.underwriterDisplayName || r.underwriterEmail || "Unknown"
          const events = Number(r[eventsField] || 0) || Number(r[fallbackEventsField] || 0) || 0
          const loans = Number(r[loansField] || 0)
          return { key, name, events, loans }
        })
        .filter(x => x.events > 0 || x.loans > 0)
        .sort((a, b) => b.events - a.events)
        .slice(0, 25)
    },

    buildSummaryFromTrend(rows, opts) {
      const eventsField = opts?.eventsField || "initialEventCount"
      const loansField = opts?.loansField || "initialDistinctLoans"

      const byUw = {}

      for (const r of rows || []) {
        const key = r.underwriterAzureId || r.underwriterDisplayName || "unknown"
        if (!byUw[key]) byUw[key] = { key, name: r.underwriterDisplayName || "Unknown", events: 0, loans: 0 }
        byUw[key].events += Number(r[eventsField] || 0)
        byUw[key].loans += Number(r[loansField] || 0)
      }

      return Object.values(byUw)
        .filter(x => x.events > 0 || x.loans > 0)
        .sort((a, b) => b.events - a.events)
        .slice(0, 25)
    },

    tryFetchAll() {
      if (!this.azureId || !this.authToken) return
      this.fetchAll()
    },

    async fetchAll() {
      const seq = ++this.requestSeq
      this.loading = true
      try {
        const { start, end } = this.chartRange

        const [initResp, condResp] = await Promise.all([
          ApiService.GetUnderwrittenLoanStatsByUnderwriter(this.azureId, start, end, this.authToken, this.orgId),
          ApiService.GetUnderwrittenConditionStatsByUnderwriter(this.azureId, start, end, this.authToken, this.orgId)
        ])

        if (seq !== this.requestSeq) return

        const initPayload = initResp?.data || {}
        const condPayload = condResp?.data || {}

        this.initialUwLeaderboard = this.extractLeaderboard(initPayload)
        this.conditionsLeaderboard = this.extractLeaderboard(condPayload)

        this.initialUwTrendRows = this.toInitialUwChartRows(this.extractDailyTrend(initPayload))
        this.conditionsTrendRows = this.toConditionsClearedChartRows(this.extractDailyTrend(condPayload))

        this.chartKey += 1
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    }
  },
  watch: {
    azureId: { immediate: true, handler() { this.tryFetchAll() } },
    authToken: { immediate: true, handler() { this.tryFetchAll() } },
    orgId() { this.tryFetchAll() },
    selectedTimeframe() { this.tryFetchAll() }
  }
}
</script>

<style scoped>
.ci-accent--productivity{ --accent-color:#2F80ED; }
.ci-accent--success{ --accent-color:#27AE60; }
.ci-accent--attention{ --accent-color:#F2994A; }
.ci-accent--insight{ --accent-color:#9B51E0; }

.ci-accent{
  border-left:10px solid var(--accent-color);
  border-top-left-radius:12px;
}

.ci-summary-title.ci-accent--productivity{
  border-bottom:1px solid var(--accent-color);
  background:rgba(47,128,237,0.10);
  color:#2F80ED;
}

.ci-summary-title.ci-accent--success{
  border-bottom:1px solid var(--accent-color);
  background:rgba(39,174,96,0.10);
  color:#27AE60;
}

.ci-chart-card-head.ci-accent--insight{
  border-bottom:1px solid var(--accent-color);
  background:rgba(155,81,224,0.10);
}

.condition-insights{
  height:100%;
  min-height:0;
  padding:12px;
  box-sizing:border-box;
  display:grid;
  grid-template-rows:auto auto auto minmax(0,1fr);
  gap:12px;
}

.ci-head{
  border:1px solid var(--border-ltr);
  border-radius:12px;
  background:rgba(5,29,58,0.03);
  padding:10px 12px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  min-height:0;
}

.ci-title{
  font-size:13px;
  font-weight:950;
  display:inline-flex;
  align-items:center;
  gap:8px;
  color:var(--text-primary-on-background);
}

.ci-title i.fa-duotone{
  --fa-primary-color:rgba(40,110,255,0.95);
  --fa-secondary-color:rgba(40,110,255,0.25);
}

.ci-toprow{
  display:grid;
grid-template-columns: 1.2fr 1.0fr 1.0fr;
  gap:12px;
  align-items:stretch;
  min-height:0;
  height:300px;
}

.ci-toprow > *{
  min-height:0;
  height:100%;
  overflow:hidden;
}

.ci-body-head{
  border:1px solid var(--border-ltr);
  border-radius:12px;
  background:rgba(5,29,58,0.03);
  padding:10px 12px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  min-height:0;
}

.ci-body-head-left{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:2px;
}

.ci-body-head-title{
  font-size:12px;
  font-weight:950;
  color:var(--text-primary-on-background);
}

.ci-body-head-sub{
  font-size:11px;
  font-weight:900;
  color:var(--text-secondary-on-background);
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.ci-select{
  border:1px solid rgba(15,23,42,0.12);
  background:rgba(255,255,255,0.7);
  border-radius:10px;
  padding:6px 10px;
  font-size:12px;
  font-weight:900;
  color:var(--text-primary-on-background);
  outline:none;
}

.ci-select:disabled{
  opacity:0.6;
  cursor:not-allowed;
}

.ci-body{
  flex:1 1 auto;
  min-height:0;
  display:grid;
  grid-template-columns:minmax(785px,460px) minmax(0,1fr);
  gap:12px;
}

.ci-left,
.ci-right{
  min-height:0;
  display:grid;
  gap:12px;
}

.ci-summary-grid{
  min-height:0;
  height:100%;
  display:grid;
  grid-template-columns:1.6fr 1fr 1fr;
  gap:12px;
  align-items:stretch;
}

.ci-summary-group{
  min-height:0;
  height:100%;
  border:1px solid var(--border-ltr);
  border-radius:12px;
  background:var(--background);
  overflow:hidden;
  display:flex;
  flex-direction:column;
}

.ci-summary-title{
  padding:10px 12px;
  border-bottom:1px solid var(--border-ltr);
  background:rgba(5,29,58,0.03);
  font-weight:950;
  font-size:12px;
  color:var(--text-primary-on-background);
  flex:0 0 auto;
  display:flex;
  align-items:center;
  justify-content:space-between;
 height:60px;
}

.ci-summary-cards{
  flex:1 1 auto;
  min-height:0;
  padding:12px;
  display:flex;
  flex-direction:column;
  gap:10px;
}

.ci-scroll{
  overflow:auto;
}

.ci-summary-loading{
  height:74px;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:950;
  color:var(--text-secondary-on-background);
}

.ci-summary-row{
  border:1px solid rgba(15,23,42,0.10);
  border-radius:5px;
  padding:10px 12px;
  background:rgba(15,23,42,0.015);
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  min-height:44px;
}

.ci-summary-main{
  min-width:0;
  flex:1 1 auto;
  display:flex;
  flex-direction:column;
  gap:6px;
}

.ci-summary-name{
  font-size:12px;
  font-weight:950;
  color:var(--text-primary-on-background);
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
  min-width:0;
}

.ci-summary-submetrics{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
}

.ci-summary-submetric{
  display:inline-flex;
  align-items:baseline;
  gap:6px;
}

.ci-pill-label{
  font-size:11px;
  font-weight:900;
  color:var(--text-secondary-on-background);
}

.ci-pill-value{
  font-size:14px;
  font-weight:950;
  color:var(--text-primary-on-background);
  line-height:1;
}

.ci-empty-inline{
  padding:18px 10px;
  text-align:center;
  font-weight:950;
  color:var(--text-secondary-on-background);
}

.ci-chart-card{
  min-height:0;
  border:1px solid var(--border-ltr);
  border-radius:12px;
  background:var(--background);
  overflow:hidden;
  display:flex;
  flex-direction:column;
}

.ci-chart-card-head{
  border-bottom:1px solid var(--border-ltr);
  background:rgba(5,29,58,0.03);
  padding:10px 12px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex:0 0 auto;
}

.ci-chart-card-head-left{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:2px;
}

.ci-chart-card-title{
  display:flex;
  align-items:center;
  gap:8px;
  font-weight:950;
  font-size:12px;
  color:var(--text-primary-on-background);
  min-width:0;
}

.ci-chart-card-sub{
  font-size:11px;
  font-weight:900;
  color:var(--text-secondary-on-background);
}

.ci-chart-toggle{
  display:inline-flex;
  padding:4px;
  border-radius:999px;
  border:1px solid rgba(15,23,42,0.10);
  background:rgba(15,23,42,0.02);
  flex:0 0 auto;
}

.ci-toggle-btn{
  border:0;
  background:transparent;
  font:inherit;
  cursor:pointer;
  padding:6px 10px;
  border-radius:999px;
  font-size:11px;
  font-weight:950;
  color:var(--text-secondary-on-background);
  white-space:nowrap;
}

.ci-toggle-btn.active{
  background:rgba(40,110,255,0.12);
  border:1px solid rgba(40,110,255,0.18);
  color:var(--text-primary-on-background);
}

.ci-toggle-btn:disabled{
  opacity:0.6;
  cursor:not-allowed;
}

.ci-chart-card-body{
  min-height:0;
  flex:1 1 auto;
  display:flex;
}

.ci-chart-wrap{
  min-height:360px;
  display:flex;
  flex:1 1 auto;
}

.ci-chart-wrap > *{
  flex:1 1 auto;
  min-height:0;
}

@media (max-width:1100px){
  /* Let the page scroll naturally — drop the fixed 4-row grid */
  .condition-insights{
    overflow-y: auto;
    height: auto;
    min-height: 100%;
    grid-template-rows: auto auto auto auto;
  }

  /* 2-up on medium screens so the 4 summary cards don't tower */
  .ci-toprow{
    grid-template-columns: repeat(2, 1fr);
    height: auto;
    min-height: 0;
  }

  .ci-toprow > *{
    height: auto;
    min-height: 140px;
  }

  /* Body stacks: leaderboards on top, chart below */
  .ci-body{
    grid-template-columns: 1fr;
    height: auto;
  }

  /* Keep leaderboards side-by-side at this width */
  .ci-summary-grid{
    grid-template-columns: repeat(2, minmax(0,1fr));
    height: auto;
  }

  /* Give each leaderboard panel a fixed height so it scrolls internally */
  .ci-summary-group{
    height: 260px;
  }

  /* Chart needs an explicit height — flex-fill doesn't work when parent has no height */
  .ci-chart-card{
    height: 420px;
  }

  .ci-chart-wrap{
    min-height: 0;
    height: 100%;
  }
}

/* Phone — everything single column */
@media (max-width:640px){
  .ci-toprow{
    grid-template-columns: 1fr;
  }

  .ci-toprow > *{
    min-height: 120px;
  }

  .ci-summary-grid{
    grid-template-columns: 1fr;
  }

  .ci-summary-group{
    height: 220px;
  }

  .ci-chart-card{
    height: 360px;
  }
}
</style>